import {
  Box,
  Chip,
  styled,
  Tooltip,
  Typography,
  useTheme,
  Fade,
  IconButton
} from '@mui/material'
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender
} from '@mui/x-data-grid'
import { OpenInNew, RestartAlt, HelpOutline } from '@mui/icons-material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import ButtonBase from 'design-system/button'
import { DiagnosticsTableProps } from '../../types'
import { DIAGNOSTICS_DETAIL } from 'navigation/CONSTANTS'
import analytics from 'services/analytics'

dayjs.extend(utc)
dayjs.extend(timezone)
import {
  CXRAY_DECISION_THRESHOLD,
  getResultConfig,
  getResultConfigByLabel,
  REVIEW_RANK_CONFIG,
  PRIORITY_RANK_CONFIG,
  CXRAY_PRIORITY_PATHOLOGIES
} from 'pages/Diagnostics/constants'
import {
  ecgTypes,
  xrayTypes
} from 'pages/Diagnostics/constants/diagnostic_types'
import { IExamResponse } from 'types/exam/types'
import DataGridBase from 'design-system/DataGrid'
import { useSuperAdmin } from 'components/rules/PermissionByRole'
import { useMemo } from 'react'

const StyledDataGrid = styled(DataGridBase<IExamResponse>)(() => ({
  '& .MuiDataGrid-topContainer': {
    '& [role="row"]': {
      backgroundColor: 'var(--primarySoft-button-color)'
    },
    '&, & svg': {
      color: '#fff'
    }
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none'
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none'
  },
  '& .MuiDataGrid-row:focus': {
    outline: 'none'
  },
  '& .MuiDataGrid-columnHeader:focus': {
    outline: 'none'
  },
  '& .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none'
  }
}))

const NoRowsOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      color: 'text.secondary'
    }}
  >
    Nenhuma análise encontrada
  </Box>
)

const ThumbnailComponent = (
  params: GridRenderCellParams<
    IExamResponse,
    string,
    any,
    GridTreeNodeWithRender
  >
) => {
  const theme = useTheme()
  if (
    !params.value ||
    params.value.trim() === '' ||
    params.row.type === 'ecg'
  ) {
    return <></>
  }

  const imageStyle = {
    maxWidth: '60px',
    maxHeight: '40px',
    objectFit: 'contain' as const,
    borderRadius: '4px',
    border: `1px solid ${theme.palette.divider}`
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        py: 1,
        position: 'relative'
      }}
    >
      <Fade in={true} timeout={300}>
        <img
          src={params.value}
          alt='Miniatura do exame'
          loading='lazy'
          style={{
            ...imageStyle,
            display: 'block'
          }}
        />
      </Fade>
    </Box>
  )
}

/**
 * Cabeçalho de coluna com um ícone de ajuda e tooltip explicativo.
 * Segue o padrão de tooltip usado no restante da tela (MUI Tooltip + arrow).
 */
const HeaderWithHelp = ({
  label,
  help
}: {
  label: string
  help: React.ReactNode
}) => (
  <Box display='flex' alignItems='center' gap={0.5}>
    <span>{label}</span>
    <Tooltip
      title={help}
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#212121',
            color: '#fff',
            maxWidth: 340,
            boxShadow: 3,
            '& .MuiTypography-root': { color: '#fff' }
          }
        },
        arrow: { sx: { color: '#212121' } }
      }}
    >
      <HelpOutline
        sx={{ fontSize: 16, cursor: 'help' }}
        onClick={e => e.stopPropagation()}
      />
    </Tooltip>
  </Box>
)

const REVIEW_HEADER_HELP = (
  <Box>
    <Typography variant='body2' mb={0.5}>
      Urgência de revisão do exame, definida pela confiança do modelo (PPV/NPV)
      da patologia mais provável.
    </Typography>
    <Typography variant='caption' display='block'>
      • Imediata: patologia provável
    </Typography>
    <Typography variant='caption' display='block'>
      • Prioritária: suspeita de patologia
    </Typography>
    <Typography variant='caption' display='block'>
      • Standard: sem achado relevante
    </Typography>
  </Box>
)

const PRIORITY_HEADER_HELP = (
  <Box>
    <Typography variant='body2' mb={0.5}>
      Gravidade clínica da patologia detectada — o maior nível entre as
      patologias do exame:
    </Typography>
    {([3, 2, 1, 0] as const).map(tier => {
      const config = PRIORITY_RANK_CONFIG[tier]
      const names = CXRAY_PRIORITY_PATHOLOGIES[tier]
        .map(code => xrayTypes[code]?.name ?? code)
        .join(', ')
      return (
        <Typography key={tier} variant='caption' display='block' mt={0.5}>
          <strong style={{ color: config.color }}>{config.label}:</strong>{' '}
          {names}
        </Typography>
      )
    })}
  </Box>
)

const DiagnosticsTable = ({
  data,
  onRowClick,
  examType,
  basePath,
  loading = false,
  paginationModel,
  onPaginationChange,
  totalCount,
  sortModel,
  onSortModelChange,
  hasCustomSort = false,
  onResetSort
}: DiagnosticsTableProps) => {
  const isSuperAdmin = useSuperAdmin()

  const formatDate = (date: string) => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return dayjs(date).tz(userTimezone).format('DD/MM/YYYY HH:mm')
  }

  /**
   * Abre o diagnóstico em uma nova aba
   */
  const handleOpenInNewTab = (examId: string, event: React.MouseEvent) => {
    event.stopPropagation() // Evitar que o clique dispare onRowClick
    if (examType === 'cxray') {
      analytics.track('xray_exam_opened', { origin: 'new_tab' })
    }
    const detailUrl = `/${basePath}/${DIAGNOSTICS_DETAIL(examId)}`
    window.open(detailUrl, '_blank')
  }

  const baseColumns: GridColDef<IExamResponse>[] = [
    // Coluna "ID Análise" (id_exam) ocultada por ora — o id continua disponível
    // na linha (usado, p.ex., pelo botão "abrir em nova aba").
    {
      field: 'patient_id',
      headerName: 'ID Paciente',
      align: 'center',
      headerAlign: 'center',
      flex: 2,
      renderCell: params => <span title={params.value}>{params.value}</span>
    },
    {
      field: 'acquisition_date',
      align: 'center',
      headerAlign: 'center',
      headerName: 'Data do Exame',
      flex: 2,
      valueGetter: (_value, row) => row.acquisition_date ?? row.created_at,
      valueFormatter: params => (params ? formatDate(params) : '-')
    },
    {
      field: 'exam_results',
      headerName: 'Resultados',
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      renderCell: params => {
        const results = params.value || []

        const examType =
          params.row.type?.toLowerCase() === 'cxray' ? 'cxray' : 'ecg'

        // CXRAY: significativo é toda predição positiva do modelo (>= threshold de decisão).
        // ECG mantém a régua própria de 0.6.
        const highProbResults = results.filter((r: { result_value: number }) =>
          examType === 'cxray'
            ? r.result_value >= CXRAY_DECISION_THRESHOLD
            : r.result_value > 0.6
        )

        // CXRAY classifica por label_result_value; ECG mantém result_value.
        const configFor = (r: {
          result_value: number
          label_result_value?: 0 | 1 | 2
        }) =>
          examType === 'cxray'
            ? getResultConfigByLabel(r.label_result_value)
            : getResultConfig(r.result_value)

        // Sort results by color for consistency
        const sortedResults = [...highProbResults].sort((a, b) =>
          configFor(a).color.localeCompare(configFor(b).color)
        )

        // Get unique colors from results
        const uniqueColors = (
          Array.from(
            new Set(sortedResults.map(r => configFor(r).color))
          ) as string[]
        )
          .sort() // Standardize color order
          .slice(0, 2) // Maximum 2 colors

        // Determine chip background style
        const chipSx: any = { cursor: 'pointer' }
        if (highProbResults.length === 0) {
          chipSx.backgroundColor = '#388e3c' // Green for 0 results
          chipSx.color = '#fff'
        } else if (uniqueColors.length === 1) {
          chipSx.backgroundColor = uniqueColors[0]
          chipSx.color = '#fff'
        } else if (uniqueColors.length === 2) {
          // Create gradient with 50/50 split
          chipSx.background = `linear-gradient(90deg, ${uniqueColors[0]} 50%, ${uniqueColors[1]} 50%)`
          chipSx.color = '#fff'
        }

        const tooltipContent = (
          <Box>
            {highProbResults.length === 0 ? (
              <Typography variant='body2'>
                Sem resultados compatíveis
              </Typography>
            ) : (
              sortedResults.map(
                (result: {
                  result_type: string
                  result_value: number
                  label_result_value?: 0 | 1 | 2
                }) => {
                  const types = examType === 'ecg' ? ecgTypes : xrayTypes
                  const typeInfo =
                    types[result.result_type as keyof typeof types]
                  const name = typeInfo?.name || result.result_type
                  const resutConfig = configFor(result)
                  return (
                    <Box key={result.result_type} mb={0.5}>
                      <Typography variant='body2'>
                        <strong>{name}: </strong>
                        <Typography variant='caption' color={resutConfig.color}>
                          <strong>{resutConfig.label}</strong>
                        </Typography>
                      </Typography>
                    </Box>
                  )
                }
              )
            )}
          </Box>
        )

        return (
          <Tooltip title={tooltipContent} arrow>
            <Chip label={highProbResults.length} size='small' sx={chipSx} />
          </Tooltip>
        )
      }
    },
    {
      field: 'diagnosis_status',
      headerName: 'Status',
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      renderCell: params => {
        const statusConfig = {
          0: { label: 'Pendente', color: 'warning' as const },
          1: { label: 'Revisado', color: 'success' as const }
        }
        const defaultConfig = {
          label: 'Desconhecido',
          color: 'default' as const
        }
        const value =
          typeof params.value === 'number'
            ? params.value
            : params.value !== undefined && params.value !== null
              ? Number(params.value)
              : undefined
        const config =
          value !== undefined && value !== null
            ? (statusConfig[value as 0 | 1] ?? defaultConfig)
            : defaultConfig

        const chip = (
          <Chip label={config.label} color={config.color} size='small' />
        )

        if (value === 0) {
          return (
            <Tooltip
              title='Revisão pendente, abra a análise e gere uma revisão para atualizar o status'
              arrow
            >
              {chip}
            </Tooltip>
          )
        }

        return chip
      }
    },
    {
      field: 'actions',
      headerName: 'Ações',
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      width: 70,
      renderCell: params => (
        <Tooltip title='Abrir em nova aba'>
          <IconButton
            size='small'
            onClick={e => handleOpenInNewTab(params.row.id_exam, e)}
            sx={{
              padding: 0,
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <OpenInNew fontSize='small' />
          </IconButton>
        </Tooltip>
      )
    }
  ]

  const columns = useMemo(() => {
    const columnsList: GridColDef<IExamResponse>[] = [...baseColumns]

    // Revisão e Prioridade são exclusivas do Raio-X; inseridas após "Resultados".
    // A ordenação da lista já é feita no servidor (order_by=review_priority), então
    // estas colunas são apenas exibição (sortable: false).
    if (examType === 'cxray') {
      const reviewPriorityColumns: GridColDef<IExamResponse>[] = [
        {
          field: 'review_rank',
          headerName: 'Revisão',
          renderHeader: () => (
            <HeaderWithHelp label='Revisão' help={REVIEW_HEADER_HELP} />
          ),
          align: 'center',
          headerAlign: 'center',
          sortable: false,
          filterable: false,
          flex: 1,
          valueGetter: (_value, row) => row.review_rank ?? 0,
          renderCell: params => {
            const config = REVIEW_RANK_CONFIG[params.value as 0 | 1 | 2]
            return (
              <Chip
                label={config.label}
                size='small'
                sx={{ backgroundColor: config.color, color: '#fff' }}
              />
            )
          }
        },
        {
          field: 'priority_rank',
          headerName: 'Prioridade',
          renderHeader: () => (
            <HeaderWithHelp label='Prioridade' help={PRIORITY_HEADER_HELP} />
          ),
          align: 'center',
          headerAlign: 'center',
          sortable: false,
          filterable: false,
          flex: 1,
          valueGetter: (_value, row) => row.priority_rank ?? 0,
          renderCell: params => {
            const config = PRIORITY_RANK_CONFIG[params.value as 0 | 1 | 2 | 3]
            return (
              <Chip
                label={config.label}
                size='small'
                sx={{ backgroundColor: config.color, color: '#fff' }}
              />
            )
          }
        }
      ]

      const resultsIndex = columnsList.findIndex(
        column => column.field === 'exam_results'
      )
      const insertAt = resultsIndex >= 0 ? resultsIndex + 1 : columnsList.length
      columnsList.splice(insertAt, 0, ...reviewPriorityColumns)
    }

    // A prévia (miniatura) só existe para Raio-X; ECG não possui imagem
    if (examType === 'cxray') {
      columnsList.unshift({
        field: 'link_original_data',
        headerName: 'Prévia',
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        renderCell: params => <ThumbnailComponent {...params} />
      })
    }

    return columnsList
  }, [isSuperAdmin, examType])

  return (
    <>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        gap={1}
        mb={2}
      >
        <Typography variant='h6'>
          Resultados ({totalCount} análises encontradas)
        </Typography>
        {!loading && hasCustomSort && onResetSort && (
          <ButtonBase variant='outlined' onClick={onResetSort}>
            <RestartAlt fontSize='small' />
            <Typography variant='body2'>Reordenar padrão</Typography>
          </ButtonBase>
        )}
      </Box>
      <StyledDataGrid
        rows={data}
        columns={columns}
        loading={loading}
        getRowId={row => row.diagnosis_id}
        onRowClick={params => onRowClick(params.row)}
        disableRowSelectionOnClick
        disableColumnMenu
        paginationMode='server'
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationChange}
        rowCount={totalCount}
        pageSizeOptions={[25, 50, 100]}
        sortingMode='server'
        sortModel={sortModel}
        onSortModelChange={model =>
          onSortModelChange(
            model
              .map(item => ({
                field: item.field,
                sort: item.sort as 'asc' | 'desc'
              }))
              .filter(item => item.sort)
          )
        }
        slots={{
          noRowsOverlay: NoRowsOverlay
        }}
        slotProps={{
          pagination: {
            labelDisplayedRows: ({ from, to, count }) =>
              `Exibindo ${from}–${to} de ${count} registros`,
            labelRowsPerPage: 'Linhas por página:'
          }
        }}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer'
          }
        }}
      />
    </>
  )
}

export default DiagnosticsTable
